import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Copy, Check } from 'lucide-react';

export const DiscountCodeGenerator = () => {
  const [code, setCode] = useState('');
  const [amount, setAmount] = useState('200');
  const [isCreating, setIsCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCode(result);
  };

  const handleCreateDiscount = async () => {
    if (!code.trim()) {
      toast.error('Vennligst skriv inn en rabattkode');
      return;
    }

    if (!amount || parseInt(amount) <= 0) {
      toast.error('Vennligst skriv inn et gyldig beløp');
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-shopify-discount', {
        body: {
          code: code.toUpperCase(),
          amount: parseInt(amount),
          currency: 'NOK'
        }
      });

      if (error) throw error;

      if (data.success) {
        setCreatedCode(code.toUpperCase());
        toast.success(data.message);
        setCode('');
      } else {
        throw new Error(data.error || 'Kunne ikke opprette rabattkode');
      }
    } catch (error: any) {
      console.error('Error creating discount:', error);
      toast.error(error.message || 'Kunne ikke opprette rabattkode');
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = () => {
    if (createdCode) {
      navigator.clipboard.writeText(createdCode);
      setCopied(true);
      toast.success('Rabattkode kopiert!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Opprett Shopify Rabattkode</CardTitle>
        <CardDescription>
          Lag engangskoder som kun kan brukes 1 gang totalt (uansett kunde)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Rabattkode</label>
          <div className="flex gap-2">
            <Input
              placeholder="F.eks. WELCOME200"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={generateRandomCode}
            >
              Generer tilfeldig
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Rabattbeløp (NOK)</label>
          <Input
            type="number"
            placeholder="200"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <Button
          onClick={handleCreateDiscount}
          disabled={isCreating || !code.trim()}
          className="w-full"
        >
          {isCreating ? 'Oppretter...' : 'Opprett rabattkode'}
        </Button>

        {createdCode && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  Rabattkode opprettet!
                </p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">
                  {createdCode}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Kan brukes 1 gang totalt • {amount} NOK rabatt
                </p>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={copyToClipboard}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
